import { useLazyGeneralReport } from 'features/Report/screens/GeneralReport/queries'
import Utils from 'general/utils/Utils'
import { useAppSelector } from 'hooks/useRedux'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Table } from 'react-bootstrap'

const parseJSONSafely = (value) => {
  if (typeof value !== 'string') return value
  try {
    return JSON.parse(value)
  } catch (error) {
    return value
  }
}

const TAX_SECTIONS = [
  {
    key: 'tax_0',
    title: 'Hàng hóa, dịch vụ chịu thuế suất thuế GTGT 0%',
    taxIndex: 0,
  },
  {
    key: 'tax_5',
    title: 'Hàng hóa, dịch vụ chịu thuế suất thuế GTGT 5%',
    taxIndex: 1,
  },
  {
    key: 'tax_8',
    title: 'Hàng hóa, dịch vụ chịu thuế suất thuế GTGT 8%',
    taxIndex: 2,
  },
  {
    key: 'tax_10',
    title: 'Hàng hóa, dịch vụ chịu thuế suất thuế GTGT 10%',
    taxIndex: 3,
  },
  {
    key: 'tax_kct',
    title: 'Hàng hóa, dịch vụ không chịu thuế giá trị gia tăng',
    taxIndex: 4,
  },
  {
    key: 'tax_kkk',
    title: 'Hàng hóa, dịch vụ không kê khai thuế',
    taxIndex: 6,
  },
  {
    key: 'tax_other',
    title: 'Hàng hóa, dịch vụ chịu thuế suất thuế GTGT khác',
    taxIndex: 5,
  },
]

const TableColGroup = () => (
  <colgroup>
    <col style={{ width: '10%' }} />
    <col style={{ width: '10%' }} />
    <col style={{ width: '15%' }} />
    <col style={{ width: '25%' }} />
    <col style={{ width: '15%' }} />
    <col style={{ width: '12.5%' }} />
    <col style={{ width: '12.5%' }} />
  </colgroup>
)

const TaxRateBody = ({
  obj,
  sysConfig,
  hasMore,
  isFetchingNextPage,
  isLoadingInitial,
  fetchNextPage,
}) => {
  const [visibleCount, setVisibleCount] = useState(10)
  const lastScrollTopRef = useRef(0)
  const lastTriggeredLengthRef = useRef(0)
  const invoiceProducts = obj?.invoiceProducts ?? []
  const hasScroll = invoiceProducts.length > 10

  useEffect(() => {
    if (isLoadingInitial) {
      setVisibleCount(10)
      lastTriggeredLengthRef.current = 0
      lastScrollTopRef.current = 0
    }
  }, [isLoadingInitial])

  useEffect(() => {
    if (invoiceProducts.length <= 10) {
      setVisibleCount(10)
    }
  }, [invoiceProducts.length])

  const visibleProducts = useMemo(() => {
    return invoiceProducts.slice(0, visibleCount)
  }, [invoiceProducts, visibleCount])

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget
    const isScrollingDown = scrollTop > lastScrollTopRef.current
    lastScrollTopRef.current = scrollTop

    if (scrollTop + clientHeight >= scrollHeight - 80) {
      if (visibleCount < invoiceProducts.length) {
        setVisibleCount((prev) => Math.min(prev + 10, invoiceProducts.length))
      }
    }
    const pagination = obj?.pagination
    const isLastPage = pagination
      ? pagination.page >= pagination.totalPages
      : false

    const remainingInMemory = invoiceProducts.length - visibleCount
    if (
      isScrollingDown &&
      !isLastPage &&
      invoiceProducts.length > 50 &&
      remainingInMemory <= 50 &&
      invoiceProducts.length > lastTriggeredLengthRef.current &&
      hasMore &&
      !isFetchingNextPage
    ) {
      lastTriggeredLengthRef.current = invoiceProducts.length
      fetchNextPage()
    }
  }

  const renderRow = (item, index) => (
    <tr key={index}>
      <td style={{ width: '10%' }} className="text-center">
        {item?.serial}
      </td>
      <td style={{ width: '10%' }} className="text-center">
        {item?.no ? Utils.padString(item?.no, 8, '0') : ''}
      </td>
      <td style={{ width: '15%' }} className="text-center">
        {item?.date}
      </td>
      <td style={{ width: '25%' }}>{item?.customerCompanyName}</td>
      <td style={{ width: '15%' }} className="text-center">
        {item?.customerTaxCode}
      </td>
      <td style={{ width: '12.5%' }} className="text-right">
        {Utils.formatNumber(
          item?.totalUnitPrice,
          ['VND', 'VNĐ'].includes(item?.currency)
            ? (parseJSONSafely(item?.numberFormat) ?? sysConfig)
                ?.currencyExchange
            : (parseJSONSafely(item?.numberFormat) ?? sysConfig)
                .foreignCurrency,
        )}
      </td>
      <td style={{ width: '12.5%' }} className="text-right">
        {Utils.formatNumber(
          item?.taxMoney,
          ['VND', 'VNĐ'].includes(item?.currency)
            ? (parseJSONSafely(item?.numberFormat) ?? sysConfig)
                ?.currencyExchange
            : (parseJSONSafely(item?.numberFormat) ?? sysConfig)
                .foreignCurrency,
        )}
      </td>
    </tr>
  )

  return (
    <>
      {hasScroll ? (
        <tr>
          <td colSpan="7" className="p-0 border-0">
            <div
              style={{
                maxHeight: '380px',
                overflowY: 'auto',
                overflowX: 'hidden',
              }}
              onScroll={handleScroll}
            >
              <table
                className="table table-bordered table-hover mb-0"
                style={{
                  tableLayout: 'fixed',
                  width: '100%',
                  borderCollapse: 'collapse',
                }}
              >
                <TableColGroup />
                <tbody>{visibleProducts.map(renderRow)}</tbody>
              </table>
            </div>
          </td>
        </tr>
      ) : (
        visibleProducts.map(renderRow)
      )}
      <tr className="font-weight-bolder table-light">
        <td colSpan="5">
          <b>Tổng cộng</b>
        </td>
        <td className="text-right">
          <b>
            {Utils.formatNumber(
              obj?.totalBeforeTax,
              sysConfig?.currencyExchange,
            )}
          </b>
        </td>
        <td className="text-right">
          <b>
            {Utils.formatNumber(obj?.taxMoney, sysConfig?.currencyExchange)}
          </b>
        </td>
      </tr>
    </>
  )
}

export default function GeneralReportTable({
  invoicesByTax: propInvoicesByTax,
  summaryTotals: propSummaryTotals,
  isLoadingInitial: propIsLoadingInitial,
  isFetchingNextPage: propIsFetchingNextPage,
  hasMore: propHasMore,
  fetchNextPage: propFetchNextPage,
}) {
  const sysConfig = useAppSelector((s) => s.systemConfig.formatNumber)
  const filter = useAppSelector((s) => s.filter.generalReport)

  const hookData = useLazyGeneralReport(filter, sysConfig ?? {})

  const invoicesByTax = propInvoicesByTax ?? hookData.invoicesByTax
  const summaryTotals = propSummaryTotals ?? hookData.summaryTotals
  const isLoadingInitial = propIsLoadingInitial ?? hookData.isLoadingInitial
  const isFetchingNextPage =
    propIsFetchingNextPage ?? hookData.isFetchingNextPage
  const hasMore = propHasMore ?? hookData.hasMore
  const fetchNextPage = propFetchNextPage ?? hookData.fetchNextPage

  return (
    <>
      <Table
        responsive
        bordered
        className="mb-0"
        style={{ tableLayout: 'fixed', width: '100%' }}
      >
        <TableColGroup />
        <thead>
          <tr>
            <th colSpan="2" className="text-center">
              Hóa đơn, Chứng từ bán
            </th>
            <th
              style={{ verticalAlign: 'middle' }}
              rowSpan="2"
              className="text-center"
            >
              Ngày, tháng, năm phát hành hóa đơn
            </th>
            <th
              style={{ verticalAlign: 'middle' }}
              rowSpan="2"
              className="text-center"
            >
              Tên khách hàng
            </th>
            <th
              style={{ verticalAlign: 'middle' }}
              rowSpan="2"
              className="text-center"
            >
              Mã số thuế khách hàng
            </th>
            <th
              style={{ verticalAlign: 'middle' }}
              rowSpan="2"
              className="text-center"
            >
              Doanh số bán chưa có thuế
            </th>
            <th
              style={{ verticalAlign: 'middle' }}
              rowSpan="2"
              className="text-center"
            >
              Thuế GTGT
            </th>
          </tr>
          <tr>
            <th className="text-center">Ký hiệu</th>
            <th className="text-center">Số hóa đơn</th>
          </tr>
        </thead>
        <tbody className="p-element p-datatable-tbody">
          {TAX_SECTIONS.map((section) => (
            <React.Fragment key={section.key}>
              <tr
                style={{ backgroundColor: '#d1ecf1' }}
                className="font-weight-bolder"
              >
                <th colSpan="8">{section.title}</th>
              </tr>
              <TaxRateBody
                obj={invoicesByTax?.[section.taxIndex]}
                sysConfig={sysConfig ?? {}}
                hasMore={hasMore}
                isFetchingNextPage={isFetchingNextPage}
                isLoadingInitial={isLoadingInitial}
                fetchNextPage={fetchNextPage}
              />
            </React.Fragment>
          ))}
          {isFetchingNextPage && (
            <tr className="text-center">
              <td colSpan="7" className="py-2 text-primary font-weight-bold">
                <i className="fa-solid fa-spinner fa-spin mr-2"></i> Đang tải
                thêm dữ liệu...
              </td>
            </tr>
          )}
        </tbody>
      </Table>

      {/* General summary */}
      <div className="w-100 d-flex flex-column gap-3 mt-4">
        <b>
          Tổng doanh thu hàng hóa, dịch vụ bán ra:
          {Utils.formatNumber(
            summaryTotals?.totalBeforeTax,
            sysConfig?.currencyExchange,
          )}
        </b>

        <b>
          Tổng số thuế GTGT của hàng hóa, dịch vụ bán ra:
          {Utils.formatNumber(
            summaryTotals?.totalTaxMoney,
            sysConfig?.currencyExchange,
          )}
        </b>

        <b>
          Tổng doanh thu hàng hóa, dịch vụ bán ra chịu thuế GTGT:
          {Utils.formatNumber(
            summaryTotals?.totalAfterTax,
            sysConfig?.currencyExchange,
          )}
        </b>
      </div>
    </>
  )
}




